import { apiService } from '../api';
import { ApiResponse, PaginatedResponse, DailyBlotter } from '../../types/api';

export interface Blotter extends DailyBlotter {}

export interface BlotterFilters {
  page?: number;
  per_page?: number;
  status?: 'pending' | 'processing' | 'completed' | 'error';
  start_date?: string;
  end_date?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  date_from?: string;
  date_to?: string;
}

export interface BlotterStatistics {
  total_blotters: number;
  completed: number;
  pending: number;
  processing: number;
  error: number;
  total_commission: number;
  total_payout: number;
  total_net: number;
  total_trades: number;
  incomplete_transactions: number;
  incomplete_commission: number;
}

class BlotterService {
  async getBlotters(params?: BlotterFilters): Promise<PaginatedResponse<Blotter>> {
    // Convert params to match backend API expectations
    const apiParams: any = {
      page: params?.page,
      per_page: params?.per_page,
    };

    if (params?.status) {
      apiParams.status = params.status;
    }

    // Backend expects start_date and end_date, not date_from/date_to
    if (params?.start_date) {
      apiParams.start_date = params.start_date;
    }
    if (params?.end_date) {
      apiParams.end_date = params.end_date;
    }

    // Also support date_from/date_to for backward compatibility
    if (params?.date_from && !apiParams.start_date) {
      apiParams.start_date = params.date_from;
    }
    if (params?.date_to && !apiParams.end_date) {
      apiParams.end_date = params.date_to;
    }

    if (params?.search) {
      apiParams.search = params.search;
    }

    return await apiService.getDailyBlotters(apiParams);
  }

  async getBlotter(id: number): Promise<ApiResponse<Blotter>> {
    return await apiService.getDailyBlotter(id);
  }

  async previewBlotter(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const headers: Record<string, string> = { 'Accept': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseURL}/blotter/preview`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return await response.json();
  }

  async uploadBlotter(
    file: File,
    date: string,
    processImmediately: boolean = true,
    /**
     * R13 Task C-r: account ids the user has explicitly opted into routing
     * 100% commission to House for. Server validates these against the
     * file's unassigned_accounts list — see BlotterController::upload.
     */
    houseOverrides: number[] = []
  ): Promise<ApiResponse<Blotter>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('date', date);
    formData.append('process_immediately', processImmediately ? '1' : '0');
    houseOverrides.forEach((id) => {
      formData.append('house_overrides[]', String(id));
    });

    // Use fetch directly since apiService.uploadCsv doesn't support custom date parameter
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const response = await fetch(`${baseURL}/blotter/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return await response.json();
  }

  /**
   * Apply client/employee assignments to new accounts identified during preview,
   * then optionally trigger blotter processing.
   */
  async applyAssignments(
    blotterId: number,
    payload: {
      auto_process: boolean;
      accounts: Array<{
        account_string: string;
        client_id?: number | null;
        user_assignments?: Array<{ user_id: number; commission_percentage: number }>;
      }>;
    }
  ): Promise<ApiResponse<any>> {
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseURL}/blotter/${blotterId}/apply-assignments`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return await response.json();
  }

  async batchUploadBlotters(
    files: File[],
    dates: string[],
    processImmediately: boolean = true
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
      formData.append(`dates[${index}]`, dates[index]);
    });
    formData.append('process_immediately', processImmediately ? '1' : '0');

    // Use fetch directly since apiService.batchUploadCsv doesn't support custom dates
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const response = await fetch(`${baseURL}/blotter/batch-upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return await response.json();
  }

  async processBlotter(id: number): Promise<ApiResponse<Blotter>> {
    // Use fetch directly since apiService doesn't have a generic process endpoint
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const response = await fetch(`${baseURL}/blotter/${id}/process`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return await response.json();
  }

  async deleteBlotter(id: number): Promise<ApiResponse<any>> {
    return await apiService.deleteBlotter(id);
  }

  async getStatistics(params?: { start_date?: string; end_date?: string }): Promise<ApiResponse<BlotterStatistics>> {
    return await apiService.getBlotterStatistics() as any;
  }

  async checkDate(date: string): Promise<ApiResponse<{ exists: boolean; date: string }>> {
    // Use fetch directly since apiService doesn't have checkDate method
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const response = await fetch(`${baseURL}/blotter/check-date`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ date })
    });

    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }

    return await response.json();
  }
}

export const blotterService = new BlotterService();
export default blotterService;
